import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, MailCheck, Clock } from "lucide-react";

export default function VerifyEmail() {
  const { token } = useParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // avoid double-consume in StrictMode
    ran.current = true;
    api.get(`/public/identity/verify/${token}`)
      .then((r) => setResult(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This verification link is not valid."));
  }, [token]);

  return (
    <div className="wyv-container py-14 sm:py-20 max-w-lg">
      <Card data-testid="verify-email-card">
        <CardContent className="p-8">
          {!result && !error && (
            <div className="space-y-4"><Skeleton className="h-10 w-10 rounded-full" /><Skeleton className="h-6 w-2/3" /><Skeleton className="h-4 w-full" /></div>
          )}
          {result && (
            <>
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                {result.state === "already_used" ? <Clock className="h-6 w-6 text-emerald-700" /> : <CheckCircle2 className="h-6 w-6 text-emerald-700" />}
              </div>
              <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="verify-email-title">
                {result.state === "verified" ? "Email confirmed!" :
                 result.state === "already_verified" ? "Already confirmed" : "Link already used"}
              </h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="verify-email-detail">{result.detail}</p>
              {result.email_verified && (
                <p className="text-sm text-foreground mt-4 flex items-start gap-2">
                  <MailCheck className="h-4 w-4 text-accent mt-0.5 shrink-0" />
                  Your email is verified. If you and your witness have witnessed each other's declarations,
                  Mutual Witness status is granted once both of you are verified.
                </p>
              )}
              <div className="flex flex-wrap gap-3 mt-6">
                <Button asChild data-testid="verify-email-home"><Link to="/">Home</Link></Button>
                <Button asChild variant="outline"><Link to="/verify">Verify a Declaration</Link></Button>
              </div>
              <p className="text-[11px] text-muted-foreground mt-5">
                Tip: your declaration page link (from your receipt) shows your live witness status.
              </p>
            </>
          )}
          {error && (
            <>
              <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center mb-4">
                <XCircle className="h-6 w-6 text-rose-700" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-foreground" data-testid="verify-email-error-title">Link not valid</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="verify-email-error-detail">{error}</p>
              <p className="text-xs text-muted-foreground mt-4">
                Open your declaration page (from your receipt) and press “Resend verification email” to get a fresh link.
              </p>
              <Button asChild variant="outline" className="mt-5"><Link to="/">Home</Link></Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
